import type { AxiosInstance } from 'axios'
import { isErrorStatus } from '@audience-republic/audience-manager-shared/api/utils'
import type { CustomApiError } from '@audience-republic/audience-manager-shared/api/types'
import { fixedEncodeURIComponent } from '@/utils/helpers'
import type { Tag } from '@/types/resources/tag'

const MAX_INITIAL_TOP_VALUE = 200

// TODO generate type and methods from API schema
export interface TagsResponse {
  rows: Tag[]
  count: number
}

export interface TagsApi {
  get (promoterOid: number, { top, filterString }: { top: number, filterString?: string }): Promise<Tag[]>

  fetchCount (promoterOid: number, { filter }: { filter?: string }): Promise<TagsResponse>

  fetchTagByOid (promoterOid: number, tagOid: number): Promise<Tag[]>

  create (promoterOid: number, name: string, favourite: boolean, external: boolean): Promise<Tag>

  merge (promoterOid: number, target: number, sources: number[]): Promise<void>

  group (promoterOid: number, target: number, sources: number[]): Promise<void>
}

export default (axios: AxiosInstance): TagsApi => ({
  /**
   * Get the Tags of the promoter with the given promoterOid
   * @param promoterOid
   * @param top
   * @param filterString
   * @returns
   */
  async get(promoterOid, { top = MAX_INITIAL_TOP_VALUE, filterString }) {
    // TODO should not be building query strings, use axios params
    let url = `/promoter/${promoterOid}/tag?$count=true&top=${top}&$orderby=count%20desc`
    if (filterString) {
      // TODO should not be building filter strings here. Logic should at least be shared
      url = `${url}&$filter=${fixedEncodeURIComponent(`name ILIKE "%${filterString}%"`)}`
    }
    const { data, status } = await axios.get(url)
    if (isErrorStatus(status)) {
      throw {
        name: 'Get Tags error',
        message: `Error getting Tags from promoter with oid of ${promoterOid}`,
        status,
      } as CustomApiError
    }
    return data.rows
  },

  async fetchCount(promoterOid, { filter }): Promise<TagsResponse> {
    const { status, data } = await axios.get<TagsResponse>(`/promoter/${promoterOid}/tag`, {
      params: {
        $select: 'oid',
        $top: 0,
        $count: true,
        $filter: filter,
      },
    })
    if (isErrorStatus(status)) {
      throw {
        name: 'Fetch Tags error',
        message: `Error fetching Tags`,
        status,
      } as CustomApiError
    }
    return data
  },

  async fetchTagByOid(promoterOid, tagOid) {
    const { data, status } = await axios.get(`/promoter/${promoterOid}/tag?$filter=oid=${tagOid}`)
    if (isErrorStatus(status)) {
      throw {
        name: 'Get Tag error',
        message: `Error fetching Tag by Oid, with a promoter with oid of ${promoterOid},
                  and tagOid of ${tagOid}.`,
        status,
      } as CustomApiError
    }
    return data
  },

  async create(promoterOid, name, favourite, external) {
    const { data, status } = await axios.post<Tag>(`/promoter/${promoterOid}/tag`, {
      name,
      'promoter-oid': promoterOid,
      favourite,
      external,
    })
    if (isErrorStatus(status)) {
      throw {
        name: 'Create Tag error',
        message: `Error creating Tag with name of ${name},
                  and promoterOid of ${promoterOid}.`,
        status,
      } as CustomApiError
    }
    return data
  },

  async merge(promoterOid, target, sources) {
    const { status } = await axios.post(`/promoter/${promoterOid}/tag/${target}/merge`, { srcTagOids: sources })
    if (isErrorStatus(status)) {
      throw {
        name: 'Merge Tags error',
        message: `Error merging Tags ${sources.join(', ')} into ${target} with promoterOid of ${promoterOid}.`,
        status,
      } as CustomApiError
    }
  },

  async group(promoterOid, target, sources) {
    const { status } = await axios.post(`/promoter/${promoterOid}/tag/${target}/group`, { tagOids: sources })
    if (isErrorStatus(status)) {
      throw {
        name: 'Group Tags error',
        message: `Error grouping Tags ${sources.join(', ')} with ${target} with promoterOid of ${promoterOid}.`,
        status,
      } as CustomApiError
    }
  },
})
